import React from 'react';
import { Box, Typography, IconButton, LinearProgress, List, ListItem, ListItemText, Dialog, DialogTitle, DialogContent, DialogActions, Button, Slider, TextField } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Speed } from '@mui/icons-material';
import scheduleService, { ScheduleItem } from '../../services/scheduleService';
import { getScheduleItemsByProjectId as getScheduleItemsAlt } from '../../services/projectService';

interface MobileScheduleTabProps {
  projectId: string;
}

const percent = (v: number | undefined | null) => {
  const n = Number(v || 0);
  return Math.max(0, Math.min(100, Math.round(n)));
};

const isCurrent = (start: string, end: string, nowIso: string) => {
  const s = new Date(start).getTime();
  const e = new Date(end).getTime();
  const n = new Date(nowIso).getTime();
  return n >= s && n <= e;
};

const MobileScheduleTab: React.FC<MobileScheduleTabProps> = ({ projectId }) => {
  const [items, setItems] = React.useState<ScheduleItem[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [currentItem, setCurrentItem] = React.useState<ScheduleItem | null>(null);
  const [progress, setProgress] = React.useState(0);
  const [progressDate, setProgressDate] = React.useState<string>('');
  const listRef = React.useRef<HTMLUListElement | null>(null);
  const todayISO = React.useMemo(() => new Date().toISOString().slice(0,10), []);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!projectId) return;
      setLoading(true);
      try {
        let list: any = await scheduleService.getScheduleItemsByProject(projectId);
        if (!Array.isArray(list) || list.length === 0) {
          try { list = await getScheduleItemsAlt(projectId); } catch {}
        }
        if (!cancelled) setItems(Array.isArray(list) ? list : (list?.data && Array.isArray(list.data) ? list.data : []));
      } catch (e) {
        console.error('Harmonogram (mobile) fetch error', e);
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  // Autoscroll do bieżącego zadania
  React.useEffect(() => {
    if (!items.length || !listRef.current) return;
    const today = new Date().toISOString().slice(0, 10);
    const idx = items.findIndex(it => isCurrent(it.start_date, it.end_date, today));
    if (idx >= 0) {
      const node = listRef.current.children[idx] as HTMLElement | undefined;
      if (node?.scrollIntoView) node.scrollIntoView({ block: 'center' });
    }
  }, [items]);

  const openProgress = (item: ScheduleItem) => {
    setCurrentItem(item);
    setProgress(percent(item.actual_progress));
    setProgressDate(todayISO);
    setDialogOpen(true);
  };

  const saveProgress = async () => {
    if (!currentItem) return;
    const dateToSend = progressDate || todayISO;
    await scheduleService.updateProgress(String(currentItem.id), { actual_progress: progress, actual_progress_date: dateToSend });
    setItems(prev => prev.map(it => it.id === currentItem.id ? { ...it, actual_progress: progress, actual_progress_date: dateToSend } : it));
    setDialogOpen(false);
    setCurrentItem(null);
  };

  const isDateInvalid = !progressDate && !todayISO;

  return (
    <Box sx={{ px: 1 }}>
      <List ref={listRef} sx={{ maxHeight: 'calc(80vh - 140px)', overflowY: 'auto' }}>
        {items.map((it) => {
          const p = percent(it.actual_progress);
          const expected = percent((it as any).expected_progress);
          const onTrack = isCurrent(it.start_date, it.end_date, new Date().toISOString().slice(0,10)) ? p >= expected : true;
          const solid = onTrack ? '#2e7d32' : '#d32f2f';
          const bgColor = alpha(solid, 0.4);
          return (
            <ListItem key={it.id} sx={{ display:'block', mb: 1, borderRadius: 1, px: 1, py: 1, position: 'relative' }}>
              {/* Siatka 4 kolumny: SSP | nazwa (+ obszar dat) | daty | ikona */}
              <Box sx={{ display:'grid', gridTemplateColumns:'24px 1fr auto auto', columnGap: 0.5 }}>
                <Typography variant="body2" sx={{ gridColumn:'1 / 2', fontWeight: 700, minWidth: 18, textAlign:'left', fontSize: '1.15rem', alignSelf:'baseline' }}>{it.ssp}</Typography>
                {/* Nazwa bezpośrednio za numerem SSP; rozciągnięta do ikony (kol.2 -> 4) */}
                <Typography variant="body2" title={(it as any).task || (it as any).name} sx={{ gridColumn:'2 / 4', whiteSpace:'normal', overflowWrap:'anywhere', wordBreak:'break-word', fontSize: '0.8rem', lineHeight: 1.2, alignSelf:'baseline', pr: 1, ml: -1 }}>{(it as any).task || (it as any).name}</Typography>
                <IconButton edge="end" onClick={() => openProgress(it)} sx={{ gridColumn:'4 / 5', justifySelf:'end', alignSelf:'start', mr: 1, transform: 'translateX(5px)' }}><Speed /></IconButton>
                {/* Drugi wiersz: Długość (chips pod SSP, kol.1) | Daty (kol.3) */}
                <Box sx={{ gridColumn:'2 / 3', display:'flex', alignItems:'center', gap: 0.75, justifySelf:'start', ml: '-26px' }}>
                  <Box sx={{ bgcolor:'#e3f2fd', color:'#1565c0', borderRadius: 9999, px: 1, py: 0.25, fontSize: '0.75rem', minWidth: 28, textAlign: 'left' }}>
                    {it.days} dni
                  </Box>
                  {typeof (it as any).delay !== 'undefined' && (
                    <Box
                      sx={{
                        bgcolor: ((it as any).delay ?? 0) > 0 ? '#e8f5e9' : (((it as any).delay ?? 0) < 0 ? '#ffebee' : '#e3f2fd'),
                        color: ((it as any).delay ?? 0) > 0 ? '#2e7d32' : (((it as any).delay ?? 0) < 0 ? '#c62828' : '#1565c0'),
                        borderRadius: 9999,
                        px: 1,
                        py: 0.25,
                        fontSize: '0.75rem',
                        minWidth: 28,
                        textAlign: 'center'
                      }}
                    >
                      {((it as any).delay ?? 0) > 0 ? `+${(it as any).delay}` : String(((it as any).delay ?? 0))}
                    </Box>
                  )}
                </Box>
                <Box sx={{ gridColumn:'3 / 4', bgcolor: '#e3f2fd', color: '#1565c0', borderRadius: 9999, px: 1, py: 0.25, fontSize: '0.75rem', whiteSpace: 'nowrap', justifySelf:'end', transform: 'translateX(50px)' }}>
                  {it.start_date} — {it.end_date}
                </Box>
              </Box>
              {/* Pasek postępu */}
              <Box sx={{ position:'relative', mt: 0.5, height: 24 }}>
                <LinearProgress variant="determinate" value={0} sx={{ height: 20, borderRadius: 10, bgcolor: 'rgba(0,0,0,0.14)' }} />
                <Box sx={{ position:'absolute', left: 0, top: 2, height: 16, borderRadius: 8, width: `${p}%`, background: bgColor }} />
                <Typography variant="caption" sx={{ position:'absolute', left: `calc(${p}% + 6px)`, bottom: 2 }}>{p}%</Typography>
              </Box>
            </ListItem>
          );
        })}
        {!loading && items.length === 0 && (
          <ListItem>
            <ListItemText primary="Brak zadań w harmonogramie" secondary="Dodaj lub zaimportuj zadania, aby wyświetlić listę" />
          </ListItem>
        )}
      </List>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Kontrola zaawansowania</DialogTitle>
        <DialogContent>
          {currentItem && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="subtitle2" color="text.secondary">Zadanie</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {currentItem.ssp} — {(currentItem as any).task || (currentItem as any).name}
              </Typography>
            </Box>
          )}
          <Typography variant="body2" sx={{ mb: 1 }}>Zaawansowanie: {progress}%</Typography>
          <TextField
            type="date"
            required
            label="Data"
            InputLabelProps={{ shrink: true }}
            value={progressDate || todayISO}
            onChange={(e)=> setProgressDate(e.target.value)}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
          />
          <Slider value={progress} onChange={(_, v)=> setProgress(v as number)} step={1} min={0} max={100} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Anuluj</Button>
          <Button variant="contained" onClick={saveProgress} disabled={!progressDate && !todayISO}>Zapisz</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MobileScheduleTab;


